import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  Badge,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Devices as DevicesIcon,
  LocationOn as LocationIcon,
  Speed as SpeedIcon,
  Battery90 as BatteryIcon,
  SignalWifi4Bar as SignalIcon,
  SignalWifiOff as SignalOffIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DirectionsCar as CarIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useApi } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { Device } from '../../types';
import { formatTimeAgo, getStatusColor, calculateDeviceHealth, getDeviceDisplayName, getDeviceBrandModel } from '../../utils/helpers';

// Fix leaflet default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom device icon based on status
const createDeviceIcon = (status: string, isOnline: boolean) => {
  let color = getStatusColor(status);
  if (!isOnline) color = '#9E9E9E';

  return L.divIcon({
    className: 'custom-div-icon',
    html: `
      <div style="
        background-color: ${color};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: 8px;
          height: 8px;
          background-color: white;
          border-radius: 50%;
        "></div>
      </div>
    `,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
};

interface DeviceMarkerProps {
  device: Device;
  onDeviceSelect: (device: Device) => void;
  isSelected: boolean;
}

const DeviceMarker: React.FC<DeviceMarkerProps> = ({ device, onDeviceSelect, isSelected }) => {
  if (!device.lastPosition?.latitude || !device.lastPosition?.longitude) {
    return null;
  }

  const isOnline = device.lastSeen && new Date(device.lastSeen) > new Date(Date.now() - 5 * 60 * 1000);
  const icon = createDeviceIcon(device.status, Boolean(isOnline));

  return (
    <Marker
      position={[device.lastPosition.latitude, device.lastPosition.longitude]}
      icon={icon}
      eventHandlers={{
        click: () => onDeviceSelect(device),
      }}
    >
      <Popup>
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {getDeviceDisplayName(device)}
          </Typography>
          {device.name && getDeviceBrandModel(device) !== device.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {getDeviceBrandModel(device)}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Cliente: {typeof device.clientId === 'object' && device.clientId ? device.clientId.name : 'Sin asignar'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            IMEI: {device.imei || 'N/A'}
          </Typography>
          {(typeof device.vehicleId === 'object' && device.vehicleId?.plate) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Placa: {typeof device.vehicleId === 'object' && device.vehicleId ? device.vehicleId.plate : ''}
            </Typography>
          )}
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2">
            Velocidad: {device.lastPosition?.speed ? `${Math.round(device.lastPosition.speed)} km/h` : 'Estático'}
          </Typography>
          <Typography variant="body2">
            Última actualización: {device.lastSeen ? formatTimeAgo(device.lastSeen) : 'Sin datos'}
          </Typography>
          <Typography variant="body2">
            Estado: <Chip
              label={device.status}
              size="small"
              sx={{
                backgroundColor: getStatusColor(device.status) + '20',
                color: getStatusColor(device.status),
              }}
            />
          </Typography>
        </Box>
      </Popup>
    </Marker>
  );
};

interface MapControllerProps {
  devices: Device[];
  selectedDevice: Device | null;
}

const MapController: React.FC<MapControllerProps> = ({ devices, selectedDevice }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedDevice?.lastPosition?.latitude && selectedDevice?.lastPosition?.longitude) {
      map.setView([selectedDevice.lastPosition.latitude, selectedDevice.lastPosition.longitude], 16);
    } else if (devices.length > 0) {
      const validDevices = devices.filter(d => d.lastPosition?.latitude && d.lastPosition?.longitude);
      if (validDevices.length > 0) {
        const bounds = L.latLngBounds(
          validDevices.map(d => [d.lastPosition!.latitude!, d.lastPosition!.longitude!])
        );
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    }
  }, [map, devices, selectedDevice]);

  return null;
};

interface DeviceListProps {
  devices: Device[];
  selectedDevice: Device | null;
  onDeviceSelect: (device: Device) => void;
  onDeviceToggle: (device: Device) => void;
  visibleDevices: Set<string>;
}

const DeviceList: React.FC<DeviceListProps> = ({
  devices,
  selectedDevice,
  onDeviceSelect,
  onDeviceToggle,
  visibleDevices,
}) => {
  const getHealthColor = (health: string) => {
    switch (health) {
      case 'excellent': return '#4CAF50';
      case 'good': return '#8BC34A';
      case 'fair': return '#FF9800';
      case 'poor': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  return (
    <List>
      {devices.map((device) => {
        const isOnline = device.lastSeen && new Date(device.lastSeen) > new Date(Date.now() - 5 * 60 * 1000);
        const deviceHealth = calculateDeviceHealth(device);
        const isVisible = visibleDevices.has(device._id);

        return (
          <ListItem
            key={device._id}
            sx={{
              border: selectedDevice?._id === device._id ? '2px solid' : '1px solid',
              borderColor: selectedDevice?._id === device._id ? 'primary.main' : 'divider',
              borderRadius: 2,
              mb: 1,
              backgroundColor: selectedDevice?._id === device._id ? 'primary.50' : 'background.paper',
            }}
          >
            <ListItemAvatar>
              <Badge
                color={isOnline ? 'success' : 'error'}
                variant="dot"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              >
                <Avatar sx={{ bgcolor: getStatusColor(device.status) }}>
                  <DevicesIcon />
                </Avatar>
              </Badge>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getDeviceDisplayName(device)}
                  </Typography>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: getHealthColor(deviceHealth),
                    }}
                  />
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {typeof device.clientId === 'object' && device.clientId ? device.clientId.name : 'Sin asignar'}
                  </Typography>
                  {(typeof device.vehicleId === 'object' && device.vehicleId?.plate) && (
                    <Typography variant="body2" color="text.secondary">
                      Placa: {typeof device.vehicleId === 'object' && device.vehicleId ? device.vehicleId.plate : ''}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {device.lastPosition?.speed ? `${Math.round(device.lastPosition.speed)} km/h` : 'Estático'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {device.lastSeen ? formatTimeAgo(device.lastSeen) : 'Sin datos'}
                  </Typography>
                </Box>
              }
              onClick={() => onDeviceSelect(device)}
              sx={{ cursor: 'pointer' }}
            />
            <IconButton
              edge="end"
              onClick={(e) => {
                e.stopPropagation();
                onDeviceToggle(device);
              }}
              color={isVisible ? 'primary' : 'default'}
            >
              {isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </ListItem>
        );
      })}
    </List>
  );
};

export const LiveMapPage: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [visibleDevices, setVisibleDevices] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [mapStyle, setMapStyle] = useState<string>('osm');

  const {
    data: devicesData,
    loading,
    error,
    execute: fetchDevices,
  } = useApi<{ devices: Device[] }>(() => apiService.getDevices({ page: 1, limit: 1000 }));

  useEffect(() => {
    if (devicesData?.devices && Array.isArray(devicesData.devices)) {
      // Initially show all devices
      const deviceIds = new Set(devicesData.devices.filter(d => d && d._id).map(d => d._id));
      setVisibleDevices(deviceIds);
    }
  }, [devicesData]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchDevices();
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchDevices]);

  const handleDeviceSelect = (device: Device) => {
    setSelectedDevice(device);
  };

  const handleDeviceToggle = (device: Device) => {
    const newVisibleDevices = new Set(visibleDevices);
    if (newVisibleDevices.has(device._id)) {
      newVisibleDevices.delete(device._id);
    } else {
      newVisibleDevices.add(device._id);
    }
    setVisibleDevices(newVisibleDevices);
  };

  const getFilteredDevices = () => {
    if (!devicesData?.devices || !Array.isArray(devicesData.devices)) return [];

    let filtered = devicesData.devices.filter(device => device && device._id);

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(device => device?.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(device =>
        (device.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (device.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (device.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (device.imei || '').includes(searchTerm) ||
        (typeof device.clientId === 'object' && device.clientId?.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (typeof device.vehicleId === 'object' && device.vehicleId?.plate?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filtered;
  };

  const filteredDevices = getFilteredDevices();
  const visibleDevicesOnMap = filteredDevices.filter(device => visibleDevices.has(device._id));

  const getMapTileLayer = () => {
    switch (mapStyle) {
      case 'satellite':
        return {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        };
      case 'terrain':
        return {
          url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
          attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
        };
      default:
        return {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        };
    }
  };

  const tileLayer = getMapTileLayer();

  if (error) {
    return (
      <Alert severity="error">
        Error cargando dispositivos: {error}
        <Button onClick={() => fetchDevices()} sx={{ ml: 2 }}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)' }}>
      {/* Sidebar */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={drawerOpen}
        sx={{
          width: 350,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 350,
            position: 'relative',
            height: '100%',
          },
        }}
      >
        <Paper sx={{ p: 2, m: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Mapa en Vivo
            </Typography>
            <IconButton onClick={() => fetchDevices()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar dispositivos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Estado"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
                <MenuItem value="maintenance">Mantenimiento</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Mapa</InputLabel>
              <Select
                value={mapStyle}
                onChange={(e) => setMapStyle(e.target.value)}
                label="Mapa"
              >
                <MenuItem value="osm">Estándar</MenuItem>
                <MenuItem value="satellite">Satélite</MenuItem>
                <MenuItem value="terrain">Terreno</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Auto refresh toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            }
            label="Actualización automática"
            sx={{ mb: 2 }}
          />

          <Divider />
        </Paper>

        {/* Device List */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DeviceList
              devices={filteredDevices}
              selectedDevice={selectedDevice}
              onDeviceSelect={handleDeviceSelect}
              onDeviceToggle={handleDeviceToggle}
              visibleDevices={visibleDevices}
            />
          )}
        </Box>

        {/* Stats */}
        <Paper sx={{ p: 2, m: 2, mt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Estadísticas
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'between' }}>
            <Typography variant="body2">
              Total: {filteredDevices.length}
            </Typography>
            <Typography variant="body2">
              Visibles: {visibleDevicesOnMap.length}
            </Typography>
            <Typography variant="body2" color="success.main">
              En línea: {filteredDevices.filter(d =>
                d.lastSeen && new Date(d.lastSeen) > new Date(Date.now() - 5 * 60 * 1000)
              ).length}
            </Typography>
          </Box>
        </Paper>
      </Drawer>

      {/* Map */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        <MapContainer
          center={[18.4861, -69.9312]} // Santo Domingo, Dominican Republic
          zoom={11}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url={tileLayer.url}
            attribution={tileLayer.attribution}
          />
          <MapController devices={visibleDevicesOnMap} selectedDevice={selectedDevice} />
          {visibleDevicesOnMap.map((device) => (
            <DeviceMarker
              key={device._id}
              device={device}
              onDeviceSelect={handleDeviceSelect}
              isSelected={selectedDevice?._id === device._id}
            />
          ))}
        </MapContainer>

        {/* Map Controls */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mb: 1 }}
          >
            {drawerOpen ? 'Ocultar Panel' : 'Mostrar Panel'}
          </Button>
        </Box>

        {/* Selected Device Info */}
        {selectedDevice && (
          <Card
            sx={{
              position: 'absolute',
              bottom: 16,
              left: drawerOpen ? 366 : 16,
              right: 16,
              zIndex: 1000,
              maxWidth: 400,
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {getDeviceDisplayName(selectedDevice)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cliente: {typeof selectedDevice.clientId === 'object' && selectedDevice.clientId ? selectedDevice.clientId.name : 'Sin asignar'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<SpeedIcon />}
                  label={`${selectedDevice.lastPosition?.speed ? Math.round(selectedDevice.lastPosition.speed) : 0} km/h`}
                  size="small"
                />
                <Chip
                  icon={selectedDevice.lastSeen && new Date(selectedDevice.lastSeen) > new Date(Date.now() - 5 * 60 * 1000) ? <SignalIcon /> : <SignalOffIcon />}
                  label={selectedDevice.lastSeen && new Date(selectedDevice.lastSeen) > new Date(Date.now() - 5 * 60 * 1000) ? 'En línea' : 'Desconectado'}
                  color={selectedDevice.lastSeen && new Date(selectedDevice.lastSeen) > new Date(Date.now() - 5 * 60 * 1000) ? 'success' : 'error'}
                  size="small"
                />
                <Chip
                  icon={<LocationIcon />}
                  label={selectedDevice.lastSeen ? formatTimeAgo(selectedDevice.lastSeen) : 'Sin datos'}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default LiveMapPage;